/**
 * Closed-set type definitions used across the microsite analytics
 * surface. Co-locating them in one file keeps the analytics package
 * directory listing tight and makes "what string-literal attributes
 * does this package emit?" answerable in a single read.
 *
 * Each enum mirrors the corresponding `enum:` list in
 * `analytics.spec.yaml` — keep the two in sync when editing either.
 */

// ---------------------------------------------------------------------------
// Sections (used by `ui.section.revealed.twgMicrositeSection`,
// `ui.section.clicked.twgMicrositeSection`, and
// `ui.section.dwelled.twgMicrositeSection`).
// ---------------------------------------------------------------------------

export enum SectionName {
	/**
	 * Logged out Hero
	 */
	Hero = 'hero',
	/**
	 * For logged out view - button-driven (non-scroll) overview section
	 * (`OverviewSection`), the successor to the scroll carousel.
	 */
	Overview = 'overview',
	/**
	 * For logged out view - 3-panel scroll carousel rendered inside the overview section.
	 */
	OverviewCarousel = 'overviewCarousel',
	/**
	 * For logged out view - Show's TWG statistics and numbers
	 */
	GraphPreviewPanel = 'graphPreviewPanel',
	/**
	 * For logged out view - "Results that speak for themselves" supplemental section.
	 */
	Results = 'results',
	/**
	 * Shared between logged-out and logged-in home - "Benchmark your AI" waitlist section, gated
	 * by `twg-microsite-team-eu-benchmark-section`. Replaces `Results` on the logged-out view.
	 */
	Benchmark = 'benchmark',
	/**
	 * For logged out view - "See your Teamwork Graph in action" supplemental section.
	 */
	GraphInAction = 'graphInAction',
	/**
	 * Shared between logged-out and logged-in experiences.
	 */
	ConnectorShowcase = 'connectorShowcase',
	/**
	 * For logged out view - "Getting Started" section (MCP/CLI)
	 */
	GettingStarted = 'gettingStarted',
	/**
	 * Shared between logged-out and logged-in home — Pricing FAQ at the
	 * bottom of the page, gated by `twg_microsite_pricing_faqs`.
	 */
	Faq = 'faq',
	/**
	 * For shared view - Footer
	 */
	Footer = 'footer',
	// Logged-in sections.
	Introduction = 'introduction',
	GraphPreview = 'graphPreview',
	ConnectedApps = 'connectedApps',
	Collaborators = 'collaborators',
	UserGraph = 'userGraph',
	// CLI page sections (used by ui.section.revealed / dwelled / clicked .twgMicrositeSection).
	CliInstallationHero = 'cliInstallationHero',
	CliTerminalShowcase = 'cliTerminalShowcase',
	CliAgentSkills = 'cliAgentSkills',
	CliStats = 'cliStats',
	CliGetStarted = 'cliGetStarted',
	// Setup page sections.
	SetupHero = 'setupHero',
	SetupCli = 'setupCli',
	SetupMcp = 'setupMcp',
	SetupConnectedGraph = 'setupConnectedGraph',
	SetupConnectorGrid = 'setupConnectorGrid',
}

/** Auth state captured on every per-section analytics event. */
export type AuthState = 'loggedIn' | 'loggedOut';

/** Which microsite page a shared section/component was rendered on. */
export type MicrositePage = 'home' | 'cli' | 'setup' | 'benchmark-waitlist';

/** OS options in the CLI install-command dropdown. Mirrors InstallPlatform in the CLI page. */
export type InstallPlatform = 'macos' | 'linux' | 'windows';

/**
 * The platform as reported to analytics. Adds `'unknown'` for a user agent that
 * matched no recognised OS; the rendering path still resolves those to Linux, so
 * it keeps using `InstallPlatform`.
 */
export type AnalyticsInstallPlatform = InstallPlatform | 'unknown';

// ---------------------------------------------------------------------------
// CLI page installation hero variants and agent-install CTAs.
// ---------------------------------------------------------------------------

export type HeaderVariant = 'control' | 'terminal_first' | 'agent_first';

export type AgentInstallAgent = 'cursor' | 'claude' | 'chatgpt';

export type AgentInstallDestination =
	| 'cursorApp'
	| 'cursorWeb'
	| 'claudeApp'
	| 'claudeWeb'
	| 'codexApp';

export type AgentInstallLaunchOutcome =
	| 'appLaunchInferred'
	| 'webFallbackOpened'
	| 'noWebFallbackShown';

// ---------------------------------------------------------------------------
// CLI page "Get started" section (used by
// `ui.getStartedItem.clicked.twgMicrositeCLIPage`).
// ---------------------------------------------------------------------------

/**
 * The six destinations offered by the CLI page's "Get started" section:
 * the three numbered action cards followed by the three "Learn more"
 * links.
 *
 * Defined as a `const` object (rather than a string `enum`) so its values
 * are bare string literals, directly assignable to the generated `itemId`
 * attribute union.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const GetStartedItemId = {
	Install: 'install',
	Authenticate: 'authenticate',
	ConnectAgent: 'connectAgent',
	GetInspired: 'getInspired',
	SeeChangelog: 'seeChangelog',
	LearnAboutTeamworkGraph: 'learnAboutTeamworkGraph',
} as const;

export type GetStartedItemId = (typeof GetStartedItemId)[keyof typeof GetStartedItemId];

/**
 * Which of the section's two interactive treatments an item uses — a
 * numbered action card or a plain "Learn more" link.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const GetStartedItemType = {
	Card: 'card',
	Link: 'link',
} as const;

export type GetStartedItemType = (typeof GetStartedItemType)[keyof typeof GetStartedItemType];

// ---------------------------------------------------------------------------
// Overview section view transitions (used by
// `ui.overviewView.shown.twgMicrositeOverview`).
// ---------------------------------------------------------------------------

/**
 * How a view in the Overview section became active.
 *
 *  - `Initial`: the first view shown when the component mounts (page load).
 *  - `Manual`: the user clicked an up/down chevron or a progress dot.
 *  - `Automatic`: the 7-second idle auto-advance fired.
 *
 * Defined as a `const` object (rather than a string `enum`) so its values are
 * the bare string literals, which are directly assignable to the generated
 * `transitionType` attribute union on
 * `ui.overviewView.shown.twgMicrositeOverview`.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const OverviewTransitionType = {
	Initial: 'initial',
	Manual: 'manual',
	Automatic: 'automatic',
} as const;

export type OverviewTransitionType =
	(typeof OverviewTransitionType)[keyof typeof OverviewTransitionType];

// ---------------------------------------------------------------------------
// CLI page agent skills view transitions (used by
// `ui.agentSkillsView.shown.twgMicrositeCLIPage`).
// ---------------------------------------------------------------------------

/**
 * The skills views offered by the CLI page's agent skills section. Mirrors the
 * `SkillsView` ids in that section's view registry, which is typed against this
 * set so a new view cannot reach analytics without being declared here.
 */
export type AgentSkillsViewId =
	| 'get-connected-context'
	| 'find-ownership'
	| 'summarize-progress'
	| 'review-engineering-work'
	| 'prepare-for-operations'
	| 'move-work-forward';

/**
 * How a view in the CLI page's agent skills section became active.
 *
 *  - `Initial`: the first view shown when the section mounts (page load).
 *  - `Manual`: the user picked a capability from the list.
 *  - `Automatic`: the 7-second auto-advance fired.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const AgentSkillsTransitionType = {
	Initial: 'initial',
	Manual: 'manual',
	Automatic: 'automatic',
} as const;

export type AgentSkillsTransitionType =
	(typeof AgentSkillsTransitionType)[keyof typeof AgentSkillsTransitionType];

/**
 * How a manual agent skills selection was made.
 *
 *  - `Click`: the user clicked a capability.
 *  - `Keyboard`: the user moved through the list with the arrow, Home or End
 *    keys, where selection follows focus.
 *  - `NotApplicable`: the view was not manually selected, so neither applies.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const AgentSkillsInteractionType = {
	Click: 'click',
	Keyboard: 'keyboard',
	NotApplicable: 'notApplicable',
} as const;

export type AgentSkillsInteractionType =
	(typeof AgentSkillsInteractionType)[keyof typeof AgentSkillsInteractionType];

// ---------------------------------------------------------------------------
// CLI page terminal showcase replays (used by
// `ui.terminalReplay.started.twgMicrositeCLIPage`,
// `ui.terminalReplay.completed.twgMicrositeCLIPage` and
// `track.terminalReplay.abandoned.twgMicrositeCLIPage`).
// ---------------------------------------------------------------------------

/**
 * The replays offered by the CLI page's terminal showcase. Mirrors the `REPLAY_IDS`
 * in that section's registry, whose scripts are typed against this set so a new
 * replay cannot reach analytics without being declared here.
 */
export type TerminalReplayId =
	| 'review-queue'
	| 'login-dependency-explanation'
	| 'login-incident-root-cause'
	| 'qa-regression-plan';

/**
 * How a terminal showcase replay began.
 *
 *  - `FirstActivation`: the visitor's first prompt pick. The terminal holds its
 *    welcome frame until then, so this is what starts the section.
 *  - `Switched`: a different prompt replaced the one on screen.
 *  - `ReplayedSame`: the replay on screen was restarted from the top.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const TerminalReplayStartType = {
	FirstActivation: 'firstActivation',
	Switched: 'switched',
	ReplayedSame: 'replayedSame',
} as const;

export type TerminalReplayStartType =
	(typeof TerminalReplayStartType)[keyof typeof TerminalReplayStartType];

/**
 * Which control started a replay. The replay button only renders once a run has
 * finished, so it always pairs with `TerminalReplayStartType.ReplayedSame`.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const TerminalReplayTrigger = {
	PromptCard: 'promptCard',
	ReplayButton: 'replayButton',
} as const;

export type TerminalReplayTrigger =
	(typeof TerminalReplayTrigger)[keyof typeof TerminalReplayTrigger];

/**
 * Why a replay ended before its final line.
 *
 *  - `Switched`: the visitor picked a different prompt mid-run.
 *  - `Replayed`: they restarted the same replay mid-run.
 *  - `PageHidden`: `visibilitychange` or `pagehide` fired mid-run.
 *  - `Unmounted`: the section left the tree with a run in flight.
 */
// oxlint-disable-next-line eslint/no-redeclare
export const TerminalReplayAbandonReason = {
	Switched: 'switched',
	Replayed: 'replayed',
	PageHidden: 'pageHidden',
	Unmounted: 'unmounted',
} as const;

export type TerminalReplayAbandonReason =
	(typeof TerminalReplayAbandonReason)[keyof typeof TerminalReplayAbandonReason];

// ---------------------------------------------------------------------------
// Tier resolution (used by `track.twgMicrositeTier.shown` and the
// optional `tier` attribute on `screen.twgMicrositeHome.viewed`).
// ---------------------------------------------------------------------------

/**
 * Which "version" of the microsite home page a visitor sees.
 *
 * Values mirror the four mutually-exclusive home-page renderings:
 *  - `LoggedOut`: marketing landing page (no auth).
 *  - `UserGraph`: tier-1 logged-in experience driven by user-level
 *    metrics (account >= 30 days, >= 5 metrics above floor).
 *  - `OrgGraph`: tier-2 logged-in experience driven by org-level
 *    metrics (>= 10 users, >= 5,000 relationships).
 *  - `GettingStarted`: tier-3 fallback for logged-in users who don't
 *    qualify for tier 1 or tier 2.
 */
export enum MicrositeTier {
	LoggedOut = 'loggedOut',
	UserGraph = 'userGraph',
	OrgGraph = 'orgGraph',
	GettingStarted = 'gettingStarted',
}

/**
 * How a `MicrositeTier` was decided for the current page load.
 *
 * Used as the `resolution` attribute on `track.twgMicrositeTier.shown`
 * so dashboards can exclude debug-overridden sessions from tier mix
 * analysis.
 *
 *  - `Organic`: tier was decided by the live gating logic in
 *    `LoggedInView` (the normal production path).
 *  - `DebugOverride`: tier was forced by the debug panel
 *    (`orgMetricsSource === 'static'` + a tier + dataset selection).
 */
export enum TierResolution {
	Organic = 'organic',
	DebugOverride = 'debugOverride',
}

// ---------------------------------------------------------------------------
// Graph explorer interaction tracking (used by
// `track.graphExplorer.firstInteracted.twgMicrositeGraphExplorer` and
// `track.graphExplorer.abandoned.twgMicrositeGraphExplorer`).
// ---------------------------------------------------------------------------

/** Closed set of "first meaningful interaction" types. */
export enum GraphInteractionType {
	NodeClick = 'nodeClick',
	NodeKeyboardSelect = 'nodeKeyboardSelect',
	CanvasPan = 'canvasPan',
	CanvasZoom = 'canvasZoom',
	EdgeClick = 'edgeClick',
	BackgroundClick = 'backgroundClick',
}

/**
 * Why the graph-explorer "abandon" detector decided the user left the
 * experience without interacting.
 */
export enum GraphAbandonReason {
	/** `document.visibilityState` flipped to `'hidden'`. */
	PageHidden = 'pageHidden',
	/** Browser tab switched but window remained focused (best-effort). */
	TabSwitched = 'tabSwitched',
	/** The explorer wrapper unmounted before any interaction. */
	Unmounted = 'unmounted',
}

// ---------------------------------------------------------------------------
// In-explorer chrome events.
// ---------------------------------------------------------------------------

/**
 * Direction of an in-explorer zoom-control click reported on
 * `ui.zoomControl.clicked.twgMicrositeGraphExplorer`.
 */
export enum ZoomDirection {
	In = 'in',
	Out = 'out',
	FitView = 'fitView',
}

/**
 * Source of an in-explorer undo click reported on
 * `ui.undo.clicked.twgMicrositeGraphExplorer`.
 *
 * - `ClearAllExpanded` — the in-explorer "Undo clear all expanded"
 *   button rendered in `GraphZoomControls` after the user clears
 *   the layout. Restores the cleared snapshot.
 * - `NoResultsHeader` — the "Go back" link rendered inside the
 *   no-results header node when an expansion returned no
 *   neighbours. Pops the last action off the action history.
 */
export enum UndoSource {
	ClearAllExpanded = 'clearAllExpanded',
	NoResultsHeader = 'noResultsHeader',
}

/**
 * How a node-dwell event on
 * `ui.node.dwelled.twgMicrositeGraphExplorer` was detected.
 *
 * - `Hover` — desktop mouse-enter / mouse-leave path. Currently the
 *   only supported value; reserved as an enum so a `Selection`
 *   (touch / keyboard) value can be added later without a breaking
 *   schema change.
 */
export enum NodeDwellTrigger {
	Hover = 'hover',
}

// ---------------------------------------------------------------------------
// Home page scroll-depth tracking.
// ---------------------------------------------------------------------------

/**
 * Closed set of scroll-depth buckets reported on
 * `ui.scrollDepth.reached.twgMicrositeHome`.
 *
 * The spec uses string values (`'25'` etc.) rather than numbers so
 * the Amplitude property is a categorical chip rather than a
 * continuous histogram.
 */
export enum ScrollDepthBucket {
	Quarter = '25',
	Half = '50',
	ThreeQuarters = '75',
	Full = '100',
}

/**
 * Buckets in the order they fire as the user scrolls down. Used by
 * the scroll-depth listener to emit each bucket at most once per page
 * load and to short-circuit once the deepest bucket is reached.
 */
export const SCROLL_DEPTH_BUCKETS_IN_ORDER: readonly ScrollDepthBucket[] = [
	ScrollDepthBucket.Quarter,
	ScrollDepthBucket.Half,
	ScrollDepthBucket.ThreeQuarters,
	ScrollDepthBucket.Full,
] as const;

// ---------------------------------------------------------------------------
// Benchmark waitlist page (used by the `*.twgMicrositeBenchmarkWaitlistPage`
// events).
// ---------------------------------------------------------------------------

/**
 * Which `/benchmark` route branch rendered the waitlist.
 *
 *  - `publicLoggedOut`: a signed-out visitor, with public benchmarking on.
 *  - `publicLoggedInWithoutCloudBenchmark`: a signed-in visitor, with public
 *    benchmarking on but the cloud benchmark off.
 *  - `debugOverride`: the Debug Panel forced the waitlist view.
 */
export type WaitlistEntryReason =
	| 'publicLoggedOut'
	| 'publicLoggedInWithoutCloudBenchmark'
	| 'debugOverride';

/** The waitlist form's fields, by name. Never their values. */
export type WaitlistFormField =
	| 'firstName'
	| 'lastName'
	| 'email'
	| 'company'
	| 'waitlistFor'
	| 'aiTools'
	| 'marketingOptIn';

/** The waitlist form's checkbox groups. */
export type WaitlistOptionGroup = 'waitlistFor' | 'aiTools' | 'marketingOptIn';

/** Stable IDs for the "I want to be added to the waitlist for" options. */
export type WaitlistInterestOptionId = 'teamworkGraphBenchmarking' | 'contextQualityScore';

/** Stable IDs for the "Which AI tools do you primarily use" options. */
export type WaitlistAiToolOptionId =
	| 'claudeCowork'
	| 'claudeCode'
	| 'claude'
	| 'codex'
	| 'chatGpt'
	| 'cursor'
	| 'copilot'
	| 'rovo'
	| 'other';

/** Any waitlist checkbox, including the marketing consent one. */
export type WaitlistOptionId = WaitlistInterestOptionId | WaitlistAiToolOptionId | 'marketingOptIn';

/** Why a started waitlist form was reported abandoned. */
export type WaitlistAbandonReason = 'pageHidden' | 'unmounted';
